import styled from '@emotion/styled';
import { colors } from '@anyroad/theme';

export const PhoneContainer = styled.div<{
  hasError?: boolean;
}>`
  display: flex;
  border: 1px solid ${(props) => (props.hasError ? '#ef4444' : '#e3e8ee')};
  border-radius: 6px;
  background-color: #ffffff;
  transition: all 0.2s ease;
  overflow: hidden;

  ${(props) =>
    !props.hasError &&
    `
    &:focus-within {
      border-color: #4f46e5;
    }
  `}
`;

export const CountrySelector = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  background-color: #f8f9fa;
  border-right: 1px solid #e3e8ee;
  cursor: pointer;
  user-select: none;
  min-width: 80px;
  padding: 0 12px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #e9ecef;
  }
`;

export const CountryDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 16px;
  color: #424770;
`;

export const FlagEmoji = styled.span`
  font-size: 16px;
  line-height: 1;
`;

export const DialCode = styled.span`
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
`;

export const DropdownArrow = styled.span`
  margin-left: 4px;
  color: #9ca3af;
  font-size: 12px;
  transition: transform 0.2s ease;
  transform: ${(props: { open: boolean }) =>
    props.open ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

export const CountryDropdown = styled.div<{ open: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e3e8ee;
  border-radius: 8px;
  z-index: 1000;
  max-height: 200px;
  overflow-y: auto;
  display: ${(props) => (props.open ? 'block' : 'none')};
`;

export const CountryOptionStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f8f9fa;
  }

  &:first-of-type {
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
  }

  &:last-of-type {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }
`;

export const CountryName = styled.span`
  font-size: 14px;
  color: #424770;
  flex: 1;
`;

export const PhoneInputStyled = styled.input`
  flex: 1;
  border: none;
  padding: 12px 16px;
  font-size: 16px;
  color: #424770;
  background: transparent;
  outline: none;

  &::placeholder {
    color: #9ca3af;
  }

  &:disabled {
    color: #6b7280;
    cursor: not-allowed;
  }
`;

// PersonalModern styled components
export const PersonalContainer = styled.div<{
  borderless?: boolean;
  isLive?: boolean;
}>`
  margin-bottom: 30px;

  ${(props) =>
    props.isLive &&
    `
    background-color: #f8f9fa;
    padding: 20px;
    border-radius: 8px;
  `}
`;

export const FormFieldsContainer = styled.div<{ borderless?: boolean }>`
  ${(props) =>
    props.borderless &&
    `
    margin-top: 16px;
  `}
`;

export const StepIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: 1px solid ${colors.text.primary};
  border-radius: 50%;
  background-color: transparent;
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text.primary};
  flex-shrink: 0;
`;

export const SectionHeader = styled.h2`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 20px;
  color: ${colors.text.primary};
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const FieldRequirementsText = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 20px;
  line-height: 1.5;
`;

export const FormSection = styled.div`
  margin-bottom: 24px;
`;

export const FormLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 400;
  color: ${colors.text.primary};
  font-size: 16px;
`;

export const HintText = styled.p`
  font-size: 14px;
  color: ${colors.text.secondary};
  margin-bottom: 12px;
  line-height: 1.4;
`;

export const InputGroup = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: 640px) {
    flex-direction: column;
    gap: 16px;
  }
`;

export const InputWrapper = styled.div`
  flex: 1;
  position: relative;
`;

export const StyledInput = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid
    ${(props) => (props.hasError ? colors.error : colors.border)};
  font-size: 16px;
  color: ${colors.text.primary};
  background-color: #ffffff;
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${colors.primary};
  }

  &::placeholder {
    color: ${colors.text.placeholder};
    font-size: 14px;
  }

  &:disabled {
    background-color: ${colors.background.light};
    color: #6b7280;
    cursor: not-allowed;
  }
`;

export const ErrorText = styled.span`
  display: block;
  color: ${colors.error};
  font-size: 14px;
  margin-top: 6px;
  line-height: 1.4;
`;

export const OptionalText = styled.span`
  color: ${colors.text.secondary};
  font-weight: normal;
  font-size: 14px;
`;

export const HiddenInput = styled.input`
  display: none;
`;
